// FR-RD14 (web tab-crash recovery): a `LocalSource` over the IndexedDB chunk store, so the
// same `runRecovery` state machine that drives desktop/mobile recovery also drives a web tab
// that closed or crashed mid-recording.
import {
  deleteLocalSession,
  getUploadState,
  listChunks,
  listUploadStates,
  putUploadState,
} from "../persist/chunkStore";
import { isSessionActive, sessionsLockedByAnyTab } from "./activeSessions";
import type { LocalSource, PersistedUploadState } from "./types";

export const webLocalSource: LocalSource = {
  async list() {
    const states = await listUploadStates();
    // A finalized-but-not-yet-deleted session shouldn't exist (deleteLocal runs right after
    // finalized is set), but skip it defensively rather than re-uploading twice. A session a
    // live recorder holds — in this tab or another tab of this origin — is not this sweep's to
    // touch (see activeSessions.ts). Failed sessions ARE listed: the state machine re-surfaces
    // them (no upload) so the UI keeps offering Export/Discard until the user picks one.
    const locked = await sessionsLockedByAnyTab();
    return states
      .filter((s) => !s.finalized && !isSessionActive(s.localSessionId) && !locked.has(s.localSessionId))
      .map((s) => s.localSessionId);
  },

  async readChunks(id) {
    const rows = await listChunks(id);
    if (rows.length === 0) {
      throw new Error(`no chunks stored locally for session ${id}`);
    }
    return rows.map((r) => r.blob);
  },

  async loadState(id) {
    return getUploadState(id);
  },

  async saveState(_id, state) {
    await putUploadState(state);
  },

  async markFailed(id, err) {
    const current = await getUploadState(id);
    if (!current) return;
    const failed: PersistedUploadState = { ...current, status: "failed", lastError: err, updatedAt: Date.now() };
    await putUploadState(failed);
  },

  async deleteLocal(id) {
    await deleteLocalSession(id);
  },
};
